import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { EmailOutboxAlert } from "@/components/EmailOutboxAlert";
import { ShieldAlert, Trash2, Eye, X, CheckCircle2, Plus, Mail, Sparkles, Wand2, Download } from "lucide-react";

export default function AdminReports() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("reports"); // reports | suggestions | corrections | products | all-reviews
  const [reports, setReports] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [allReviews, setAllReviews] = useState({ items: [], total: 0 });
  const [reviewsKind, setReviewsKind] = useState("all"); // all | school | product
  const [reviewsQuery, setReviewsQuery] = useState("");
  const [reviewsSkip, setReviewsSkip] = useState(0);
  const REVIEWS_PAGE = 50;
  const [filter, setFilter] = useState("open");
  const [stats, setStats] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [aiExtract, setAiExtract] = useState({}); // suggestion_id -> extracted data
  const [aiLoading, setAiLoading] = useState({}); // suggestion_id -> bool

  useEffect(() => {
    if (loading) return;
    if (!user || !user.is_admin) {
      navigate("/");
      return;
    }
    document.title = "Moderazione — Per manina";
  }, [user, loading, navigate]);

  const load = async () => {
    setLoadingData(true);
    try {
      const s = await api.get("/admin/stats");
      setStats(s.data);
      if (tab === "reports") {
        const r = await api.get("/admin/reports", { params: { status: filter } });
        setReports(r.data);
      } else if (tab === "suggestions") {
        const sg = await api.get("/admin/suggestions", { params: { status: filter } });
        setSuggestions(sg.data);
      } else if (tab === "products") {
        const pp = await api.get("/admin/product-suggestions", { params: { status: filter } });
        setProductSuggestions(pp.data);
      } else if (tab === "all-reviews") {
        const ar = await api.get("/admin/all-reviews", {
          params: { kind: reviewsKind, q: reviewsQuery || undefined, limit: REVIEWS_PAGE, skip: reviewsSkip },
        });
        setAllReviews(ar.data);
      } else {
        const co = await api.get("/admin/corrections", { params: { status: filter } });
        setCorrections(co.data);
      }
    } catch {
      toast.error("Impossibile caricare i dati");
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) load();
    // eslint-disable-next-line
  }, [user, filter, tab, reviewsKind, reviewsQuery, reviewsSkip]);

  const resolve = async (id, action) => {
    if (action === "delete" && !window.confirm("Eliminare il contenuto segnalato?")) return;
    try {
      await api.post(`/admin/reports/${id}/resolve`, { action });
      toast.success(action === "delete" ? "Contenuto eliminato" : "Segnalazione archiviata");
      load();
    } catch { toast.error("Errore"); }
  };

  const resolveSuggestion = async (id, action) => {
    if (action === "approve" && !window.confirm("Approvare e creare la scuola nel database?")) return;
    try {
      const { data } = await api.post(`/admin/suggestions/${id}/resolve`, { action });
      toast.success(action === "approve" ? "Scuola creata e pubblicata" : "Suggerimento rifiutato");
      if (action === "approve" && data?.school_id) {
        // Optional: open it
      }
      load();
    } catch { toast.error("Errore"); }
  };

  const aiExtractFromSite = async (suggestion) => {
    if (!suggestion.website) { toast.error("Nessun sito web da analizzare"); return; }
    setAiLoading({ ...aiLoading, [suggestion.id]: true });
    try {
      const { data } = await api.post("/admin/scrape-url", { url: suggestion.website });
      setAiExtract({ ...aiExtract, [suggestion.id]: data.data });
      toast.success("Dati estratti dal sito");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore estrazione AI");
    } finally {
      setAiLoading({ ...aiLoading, [suggestion.id]: false });
    }
  };

  const resolveCorrection = async (id, action, fields) => {
    if (action === "apply" && !window.confirm("Applicare la correzione alla scheda della scuola?")) return;
    try {
      await api.post(`/admin/corrections/${id}/resolve`, { action, fields });
      toast.success(action === "apply" ? "Correzione applicata" : "Correzione rifiutata");
      load();
    } catch { toast.error("Errore"); }
  };

  const resolveProductSuggestion = async (id, action) => {
    if (action === "approve" && !window.confirm("Approvare e creare il prodotto nel catalogo?")) return;
    try {
      const payload = { action };
      if (action === "approve" && productEdits[id]) {
        payload.overrides = productEdits[id];
      }
      const { data } = await api.post(`/admin/product-suggestions/${id}/resolve`, payload);
      toast.success(action === "approve" ? `Prodotto pubblicato (${data.slug})` : "Suggerimento rifiutato");
      setProductEdits((prev) => { const n = { ...prev }; delete n[id]; return n; });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  // Available product categories (loaded once, used in editable admin dropdowns)
  const [productCategories, setProductCategories] = useState([]);
  const [productEdits, setProductEdits] = useState({}); // suggestion_id -> {field: value}
  const [editingProduct, setEditingProduct] = useState({}); // suggestion_id -> {name, category, kind, ...}
  const [savingEdit, setSavingEdit] = useState({}); // suggestion_id -> bool

  useEffect(() => {
    api.get("/products/categories").then(({ data }) => setProductCategories(data || [])).catch(() => {});
  }, []);

  const updateProductEdit = (suggestionId, field, value) => {
    setProductEdits((prev) => ({
      ...prev,
      [suggestionId]: { ...(prev[suggestionId] || {}), [field]: value },
    }));
  };

  const startEditPublished = (suggestion) => {
    // Prefill from current suggestion data (which matches the published product)
    setEditingProduct((prev) => ({
      ...prev,
      [suggestion.id]: {
        name: suggestion.name || "",
        category: suggestion.category || "",
        kind: suggestion.kind || "libro",
        age_min: suggestion.age_min ?? 3,
        age_max: suggestion.age_max ?? 6,
        brand_or_author: suggestion.brand_or_author || "",
        description: suggestion.description || "",
        utility_note: suggestion.utility_note || "",
        store_link: suggestion.store_link || "",
      },
    }));
  };

  const cancelEditPublished = (suggestionId) => {
    setEditingProduct((prev) => { const n = { ...prev }; delete n[suggestionId]; return n; });
  };

  const savePublishedProductEdit = async (suggestion) => {
    const productId = suggestion.linked_product_id;
    if (!productId) return toast.error("Prodotto non collegato");
    const edits = editingProduct[suggestion.id];
    if (!edits) return;
    setSavingEdit((prev) => ({ ...prev, [suggestion.id]: true }));
    try {
      const { data } = await api.patch(`/admin/products/${productId}`, edits);
      toast.success(`Prodotto aggiornato (${data.product?.slug || productId})`);
      cancelEditPublished(suggestion.id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiornamento");
    } finally {
      setSavingEdit((prev) => ({ ...prev, [suggestion.id]: false }));
    }
  };

  const adminDeleteReview = async (kind, id, userName) => {
    if (!window.confirm(`Eliminare definitivamente l'esperienza di ${userName}?\n\nAzione irreversibile.`)) return;
    try {
      await api.delete(`/admin/reviews/${kind}/${id}`);
      toast.success("Esperienza eliminata");
      load();
    } catch { toast.error("Errore nell'eliminazione"); }
  };

  if (loading || !user || !user.is_admin) {
    return <div className="max-w-7xl mx-auto px-4 py-20 font-ui text-[#5C6B64]">Caricamento…</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-reports">
      <div className="flex items-center gap-3 mb-2">
        <ShieldAlert className="text-[#C05C45]" size={28} />
        <p className="label-eyebrow text-[#C05C45]">Moderazione</p>
      </div>
      <h1 className="font-display text-4xl text-[#1A362D] tracking-tight">Moderazione della community</h1>
      <EmailOutboxAlert />

      {/* Stats */}
      {stats && (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            ["Segnalazioni aperte", stats.reports_open, "text-[#C05C45]"],
            ["Suggerimenti aperti", stats.suggestions_open, "text-[#C05C45]"],
            ["Correzioni aperte", stats.corrections_open, "text-[#C05C45]"],
            ["Iscritti", stats.users, "text-[#1A362D]"],
          ].map(([l, v, c]) => (
            <div key={l} className="card-surface p-4">
              <p className="label-eyebrow">{l}</p>
              <p className={`mt-1 font-display text-3xl ${c}`}>{v}</p>
            </div>
          ))}
        </div>
      )}

      {/* Marketing contacts export */}
      {stats && (
        <div className="mt-4 card-surface p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="marketing-export-panel">
          <div>
            <p className="label-eyebrow text-[#C05C45]">Newsletter & Marketing</p>
            <p className="mt-1 font-ui text-sm text-[#1A362D]">
              <span data-testid="marketing-contacts-count" className="font-semibold">{stats.marketing_consents || 0}</span> contatti hanno espresso consenso al marketing (newsletter, comunicazioni promozionali).
            </p>
            <p className="font-ui text-xs text-[#8A9691] mt-0.5">Esporta l'elenco in CSV (apribile con Excel/Numbers) per importarlo nel tuo CRM quando vorrai.</p>
          </div>
          <button
            onClick={async () => {
              try {
                const r = await api.get("/admin/export/marketing-contacts.csv", { responseType: "blob" });
                const blob = new Blob([r.data], { type: "text/csv;charset=utf-8;" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                const today = new Date().toISOString().slice(0, 10);
                a.href = url;
                a.download = `permanina-marketing-contacts-${today}.csv`;
                a.click();
                window.URL.revokeObjectURL(url);
                toast.success(`${stats.marketing_consents || 0} contatti esportati`);
              } catch {
                toast.error("Errore nell'export");
              }
            }}
            data-testid="marketing-export-btn"
            disabled={!stats.marketing_consents}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <Download size={15} /> Esporta CSV
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="mt-8 flex gap-2 border-b border-[#E8E6E1] flex-wrap">
        <button onClick={() => { setTab("reports"); setFilter("open"); }} data-testid="tab-reports" className={`px-4 py-2 font-ui text-sm border-b-2 -mb-px transition-colors ${tab === "reports" ? "border-[#C05C45] text-[#C05C45]" : "border-transparent text-[#5C6B64] hover:text-[#1A362D]"}`}>
          Segnalazioni abusi
        </button>
        <button onClick={() => { setTab("suggestions"); setFilter("open"); }} data-testid="tab-suggestions" className={`px-4 py-2 font-ui text-sm border-b-2 -mb-px transition-colors ${tab === "suggestions" ? "border-[#C05C45] text-[#C05C45]" : "border-transparent text-[#5C6B64] hover:text-[#1A362D]"}`}>
          Suggerimenti scuole
        </button>
        <button onClick={() => { setTab("corrections"); setFilter("open"); }} data-testid="tab-corrections" className={`px-4 py-2 font-ui text-sm border-b-2 -mb-px transition-colors ${tab === "corrections" ? "border-[#C05C45] text-[#C05C45]" : "border-transparent text-[#5C6B64] hover:text-[#1A362D]"}`}>
          Correzioni
        </button>
        <button onClick={() => { setTab("products"); setFilter("open"); }} data-testid="tab-products" className={`px-4 py-2 font-ui text-sm border-b-2 -mb-px transition-colors ${tab === "products" ? "border-[#C05C45] text-[#C05C45]" : "border-transparent text-[#5C6B64] hover:text-[#1A362D]"}`}>
          Suggerimenti prodotti {stats?.product_suggestions_open > 0 && <span className="ml-1 text-[10px] bg-[#C05C45] text-white px-1.5 py-0.5 rounded-full">{stats.product_suggestions_open}</span>}
        </button>
        <button onClick={() => { setTab("all-reviews"); setReviewsSkip(0); }} data-testid="tab-all-reviews" className={`px-4 py-2 font-ui text-sm border-b-2 -mb-px transition-colors ${tab === "all-reviews" ? "border-[#C05C45] text-[#C05C45]" : "border-transparent text-[#5C6B64] hover:text-[#1A362D]"}`}>
          Tutte le esperienze
        </button>
      </div>

      {/* Filter — hidden on all-reviews tab (uses its own filters) */}
      {tab !== "all-reviews" && (
        <div className="mt-6 flex gap-2 flex-wrap">
          {(tab === "reports"
            ? [{v:"open",l:"Aperte"},{v:"dismissed",l:"Archiviate"},{v:"deleted",l:"Eliminate"},{v:"all",l:"Tutte"}]
            : tab === "suggestions" || tab === "products"
            ? [{v:"open",l:"In attesa"},{v:"approved",l:"Approvate"},{v:"rejected",l:"Rifiutate"},{v:"all",l:"Tutte"}]
            : [{v:"open",l:"In attesa"},{v:"applied",l:"Applicate"},{v:"rejected",l:"Rifiutate"},{v:"all",l:"Tutte"}]
          ).map((opt) => (
            <button key={opt.v} onClick={() => setFilter(opt.v)} data-testid={`filter-status-${opt.v}`} className={`px-3 py-1.5 rounded-md text-xs font-ui border transition-colors ${filter === opt.v ? "bg-[#1A362D] text-white border-[#1A362D]" : "border-[#E8E6E1] text-[#1A362D] hover:border-[#1A362D]"}`}>
              {opt.l}
            </button>
          ))}
        </div>
      )}

      {/* All-reviews filters */}
      {tab === "all-reviews" && (
        <div className="mt-6 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="flex gap-2">
            {[{v:"all",l:"Tutte"},{v:"school",l:"🏫 Scuole"},{v:"product",l:"📚 Libri & Giochi"}].map((opt) => (
              <button
                key={opt.v}
                onClick={() => { setReviewsKind(opt.v); setReviewsSkip(0); }}
                data-testid={`all-reviews-kind-${opt.v}`}
                className={`px-3 py-1.5 rounded-md text-xs font-ui border transition-colors ${reviewsKind === opt.v ? "bg-[#1A362D] text-white border-[#1A362D]" : "border-[#E8E6E1] text-[#1A362D] hover:border-[#1A362D]"}`}
              >
                {opt.l}
              </button>
            ))}
          </div>
          <input
            type="search"
            data-testid="all-reviews-search"
            value={reviewsQuery}
            onChange={(e) => { setReviewsQuery(e.target.value); setReviewsSkip(0); }}
            placeholder="Cerca per commento, autore o nome scuola/prodotto…"
            className="flex-1 bg-white border border-[#E8E6E1] rounded-md px-3 py-2 text-sm font-ui focus:outline-none focus:border-[#C05C45]"
          />
        </div>
      )}

      {/* List */}
      <div className="mt-6 space-y-4" data-testid="moderation-list">
        {loadingData ? (
          <div className="card-surface p-10 text-center font-ui text-[#5C6B64]">Caricamento…</div>
        ) : tab === "reports" ? (
          reports.length === 0 ? (
            <div className="card-surface p-10 text-center">
              <CheckCircle2 className="mx-auto text-[#1A362D]" size={32} />
              <p className="mt-3 font-display text-2xl text-[#1A362D]">Nessuna segnalazione</p>
            </div>
          ) : reports.map((r) => (
            <div key={r.id} className="card-surface p-5" data-testid={`report-${r.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wider font-ui bg-[#DCE4DC] text-[#1A362D] px-2 py-0.5 rounded">{{ review: "Esperienza scuola", reply: "Risposta", product_review: "Commento prodotto" }[r.target_type] || r.target_type}</span>
                  <span className={`text-[10px] uppercase tracking-wider font-ui px-2 py-0.5 rounded ${r.status === "open" ? "bg-[#C05C45] text-white" : r.status === "deleted" ? "bg-[#B33A3A] text-white" : "bg-[#E8E6E1] text-[#5C6B64]"}`}>{r.status}</span>
                  <span className="text-xs text-[#8A9691] font-ui">{new Date(r.created_at).toLocaleString("it-IT")}</span>
                </div>
                {r.product && <Link to={`/prodotto/${r.product.slug}`} className="text-xs font-ui text-[#C05C45] hover:underline inline-flex items-center gap-1"><Eye size={11} /> {r.product.name}</Link>}
                {r.school && <Link to={`/scuola/${r.school.id}`} className="text-xs font-ui text-[#C05C45] hover:underline inline-flex items-center gap-1"><Eye size={11} /> {r.school.name} — {r.school.city}</Link>}
              </div>
              <p className="mt-3 font-ui text-sm text-[#5C6B64]"><span className="text-[#1A362D] font-medium">Motivo:</span> {r.reason}</p>
              <p className="mt-1 font-ui text-xs text-[#8A9691]">Segnalato da: {r.reporter_name} ({r.reporter_email})</p>
              {r.target && (
                <div className="mt-4 p-4 bg-[#F2EFEB] rounded-md border border-[#E8E6E1]">
                  <p className="text-xs font-ui text-[#8A9691] mb-1">Contenuto — di {r.target.user_name}{r.target.rating ? ` (${r.target.rating}★)` : ""}:</p>
                  <p className="font-ui text-sm text-[#1A362D] whitespace-pre-line">{r.target.comment}</p>
                </div>
              )}
              {r.status === "open" && (
                <div className="mt-4 flex gap-2">
                  <button onClick={() => resolve(r.id, "delete")} data-testid={`report-delete-${r.id}`} className="btn-danger text-xs px-3 py-1.5 inline-flex items-center gap-1"><Trash2 size={12} /> Elimina</button>
                  <button onClick={() => resolve(r.id, "dismiss")} data-testid={`report-dismiss-${r.id}`} className="btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1"><X size={12} /> Archivia</button>
                </div>
              )}
            </div>
          ))
        ) : tab === "suggestions" ? (
          suggestions.length === 0 ? (
            <div className="card-surface p-10 text-center">
              <CheckCircle2 className="mx-auto text-[#1A362D]" size={32} />
              <p className="mt-3 font-display text-2xl text-[#1A362D]">Nessun suggerimento</p>
              <p className="mt-2 font-ui text-sm text-[#5C6B64]">Quando i genitori segnalano scuole mancanti, le trovi qui.</p>
            </div>
          ) : suggestions.map((s) => (
            <div key={s.id} className="card-surface p-5" data-testid={`suggestion-${s.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wider font-ui bg-[#DCE4DC] text-[#1A362D] px-2 py-0.5 rounded">{s.type}</span>
                  <span className={`text-[10px] uppercase tracking-wider font-ui px-2 py-0.5 rounded ${s.status === "open" ? "bg-[#C05C45] text-white" : s.status === "approved" ? "bg-[#1A362D] text-white" : "bg-[#E8E6E1] text-[#5C6B64]"}`}>{s.status}</span>
                  <span className="text-xs text-[#8A9691] font-ui">{new Date(s.created_at).toLocaleString("it-IT")}</span>
                </div>
                {s.linked_school_id && <Link to={`/scuola/${s.linked_school_id}`} className="text-xs font-ui text-[#C05C45] hover:underline inline-flex items-center gap-1"><Eye size={11} /> Scheda creata</Link>}
              </div>
              <h3 className="mt-3 font-display text-xl text-[#1A362D]">{s.name}</h3>
              <p className="mt-1 font-ui text-sm text-[#5C6B64]">{s.address} — {s.postal_code} {s.city} ({s.province})</p>
              {(s.phone || s.email || s.website) && (
                <div className="mt-2 font-ui text-xs text-[#5C6B64] flex flex-wrap gap-x-4 gap-y-1">
                  {s.phone && <span>📞 {s.phone}</span>}
                  {s.email && <span>✉️ {s.email}</span>}
                  {s.website && <a href={s.website} target="_blank" rel="noreferrer" className="text-[#C05C45] hover:underline">🌐 {s.website}</a>}
                </div>
              )}
              {s.notes && <p className="mt-3 p-3 bg-[#F2EFEB] rounded-md font-ui text-sm text-[#1A362D]">{s.notes}</p>}
              <p className="mt-3 font-ui text-xs text-[#8A9691] inline-flex items-center gap-1"><Mail size={11} /> Da: {s.contact_name || "—"} ({s.contact_email})</p>

              {/* AI extraction panel */}
              {s.website && s.status === "open" && (
                <div className="mt-4 p-3 bg-[#FDF6EE] border border-[#E19A32]/40 rounded-md">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-xs font-ui inline-flex items-center gap-1 text-[#1A362D]"><Sparkles size={12} className="text-[#E19A32]" /> Estrazione AI dal sito web</p>
                    <button onClick={() => aiExtractFromSite(s)} disabled={aiLoading[s.id]} data-testid={`ai-extract-${s.id}`} className="btn-secondary text-xs px-3 py-1 inline-flex items-center gap-1 disabled:opacity-50">
                      <Wand2 size={11} /> {aiLoading[s.id] ? "Analisi…" : (aiExtract[s.id] ? "Riprova" : "Analizza sito")}
                    </button>
                  </div>
                  {aiExtract[s.id] && (
                    <pre className="mt-2 text-[11px] font-ui bg-white border border-[#E8E6E1] rounded p-2 overflow-auto text-[#1A362D] whitespace-pre-wrap">{JSON.stringify(aiExtract[s.id], null, 2)}</pre>
                  )}
                </div>
              )}

              {s.status === "open" && (
                <div className="mt-4 flex gap-2">
                  <button onClick={() => resolveSuggestion(s.id, "approve")} data-testid={`suggestion-approve-${s.id}`} className="btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1"><Plus size={12} /> Approva e crea</button>
                  <button onClick={() => resolveSuggestion(s.id, "reject")} data-testid={`suggestion-reject-${s.id}`} className="btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1"><X size={12} /> Rifiuta</button>
                </div>
              )}
            </div>
          ))
        ) : null}

        {/* Product suggestions tab */}
        {tab === "products" && !loadingData && (
          productSuggestions.length === 0 ? (
            <div className="card-surface p-10 text-center">
              <CheckCircle2 className="mx-auto text-[#1A362D]" size={32} />
              <p className="mt-3 font-display text-2xl text-[#1A362D]">Nessun suggerimento prodotto</p>
              <p className="mt-2 font-ui text-sm text-[#5C6B64]">Quando i genitori segnalano libri o giochi, li trovi qui.</p>
            </div>
          ) : productSuggestions.map((s) => {
            const editing = editingProduct[s.id];
            const edit = productEdits[s.id] || {};
            const effCategory = edit.category ?? s.category;
            const effKind = edit.kind ?? s.kind;
            const effAgeMin = edit.age_min ?? s.age_min;
            const effAgeMax = edit.age_max ?? s.age_max;
            return (
            <div key={s.id} className="card-surface p-5" data-testid={`product-suggestion-${s.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wider font-ui bg-[#DCE4DC] text-[#1A362D] px-2 py-0.5 rounded">{effKind}</span>
                  <span className="text-[10px] uppercase tracking-wider font-ui bg-[#F2EFEB] text-[#1A362D] px-2 py-0.5 rounded">{effCategory}</span>
                  <span className={`text-[10px] uppercase tracking-wider font-ui px-2 py-0.5 rounded ${s.status === "open" ? "bg-[#C05C45] text-white" : s.status === "approved" ? "bg-[#1A362D] text-white" : "bg-[#E8E6E1] text-[#5C6B64]"}`}>{s.status}</span>
                  <span className="text-xs text-[#8A9691] font-ui">{new Date(s.created_at).toLocaleString("it-IT")}</span>
                </div>
                {s.linked_product_id && <span className="text-xs font-ui text-[#1A362D]">✓ Pubblicato</span>}
              </div>
              <h3 className="mt-3 font-display text-xl text-[#1A362D]">{s.name}</h3>
              {s.brand_or_author && <p className="mt-1 font-ui text-sm text-[#5C6B64]">{s.brand_or_author}</p>}
              <p className="mt-1 font-ui text-xs text-[#8A9691]">Fascia età: {effAgeMin}-{effAgeMax} anni</p>
              <p className="mt-3 p-3 bg-[#F2EFEB] rounded-md font-ui text-sm text-[#1A362D]">{s.description}</p>
              {s.utility_note && <p className="mt-2 p-3 bg-[#FFF8F0] border-l-4 border-[#C05C45] rounded-r-md font-ui text-sm text-[#1A362D]"><span className="text-[#C05C45] font-medium">Utilità: </span>{s.utility_note}</p>}
              {s.store_link && <a href={s.store_link} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs font-ui text-[#C05C45] hover:underline">🌐 {s.store_link.slice(0, 80)}…</a>}
              <p className="mt-3 font-ui text-xs text-[#8A9691] inline-flex items-center gap-1"><Mail size={11} /> Da: {s.user_name} ({s.user_email})</p>

              {/* Editable overrides BEFORE approval — root-cause fix for wrong categories */}
              {s.status === "open" && (
                <div className="mt-4 p-3 bg-[#FBFAF7] border border-[#E8E6E1] rounded-md" data-testid={`product-suggestion-overrides-${s.id}`}>
                  <p className="font-ui text-xs font-semibold text-[#1A362D] mb-2">✏️ Correggi prima di approvare (opzionale)</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <label className="text-xs text-[#5C6B64]">
                      Categoria
                      <select
                        value={effCategory}
                        onChange={(e) => updateProductEdit(s.id, "category", e.target.value)}
                        data-testid={`override-category-${s.id}`}
                        className="mt-1 w-full bg-white border border-[#E8E6E1] rounded px-2 py-1 text-sm"
                      >
                        {productCategories.map((c) => (
                          <option key={c.slug} value={c.slug}>{c.emoji} {c.label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="text-xs text-[#5C6B64]">
                      Tipo
                      <select
                        value={effKind}
                        onChange={(e) => updateProductEdit(s.id, "kind", e.target.value)}
                        data-testid={`override-kind-${s.id}`}
                        className="mt-1 w-full bg-white border border-[#E8E6E1] rounded px-2 py-1 text-sm"
                      >
                        <option value="libro">📖 Libro</option>
                        <option value="gioco">🧩 Gioco</option>
                        <option value="app">📱 App</option>
                      </select>
                    </label>
                    <label className="text-xs text-[#5C6B64]">
                      Età min
                      <input type="number" min={0} max={18} value={effAgeMin}
                        onChange={(e) => updateProductEdit(s.id, "age_min", Number(e.target.value))}
                        data-testid={`override-age-min-${s.id}`}
                        className="mt-1 w-full bg-white border border-[#E8E6E1] rounded px-2 py-1 text-sm" />
                    </label>
                    <label className="text-xs text-[#5C6B64]">
                      Età max
                      <input type="number" min={0} max={18} value={effAgeMax}
                        onChange={(e) => updateProductEdit(s.id, "age_max", Number(e.target.value))}
                        data-testid={`override-age-max-${s.id}`}
                        className="mt-1 w-full bg-white border border-[#E8E6E1] rounded px-2 py-1 text-sm" />
                    </label>
                  </div>
                </div>
              )}

              {s.status === "open" && (
                <div className="mt-4 flex gap-2">
                  <button onClick={() => resolveProductSuggestion(s.id, "approve")} data-testid={`product-suggestion-approve-${s.id}`} className="btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1"><Plus size={12} /> Approva e pubblica</button>
                  <button onClick={() => resolveProductSuggestion(s.id, "reject")} data-testid={`product-suggestion-reject-${s.id}`} className="btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1"><X size={12} /> Rifiuta</button>
                </div>
              )}

              {/* Edit already-published community product */}
              {s.status === "approved" && s.linked_product_id && !editing && (
                <div className="mt-4">
                  <button
                    onClick={() => startEditPublished(s)}
                    data-testid={`edit-published-product-${s.id}`}
                    className="btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1"
                  >
                    🔧 Modifica prodotto pubblicato
                  </button>
                </div>
              )}
              {s.status === "approved" && editing && (
                <div className="mt-4 p-3 bg-[#FBFAF7] border border-[#C05C45] rounded-md" data-testid={`edit-form-${s.id}`}>
                  <p className="font-ui text-xs font-semibold text-[#C05C45] mb-2">🔧 Modifica prodotto pubblicato</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <label className="text-xs text-[#5C6B64] sm:col-span-2">
                      Nome
                      <input value={editing.name}
                        onChange={(e) => setEditingProduct((prev) => ({ ...prev, [s.id]: { ...prev[s.id], name: e.target.value } }))}
                        data-testid={`edit-name-${s.id}`}
                        className="mt-1 w-full bg-white border border-[#E8E6E1] rounded px-2 py-1 text-sm" />
                    </label>
                    <label className="text-xs text-[#5C6B64]">
                      Categoria
                      <select value={editing.category}
                        onChange={(e) => setEditingProduct((prev) => ({ ...prev, [s.id]: { ...prev[s.id], category: e.target.value } }))}
                        data-testid={`edit-category-${s.id}`}
                        className="mt-1 w-full bg-white border border-[#E8E6E1] rounded px-2 py-1 text-sm">
                        {productCategories.map((c) => (
                          <option key={c.slug} value={c.slug}>{c.emoji} {c.label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="text-xs text-[#5C6B64]">
                      Tipo
                      <select value={editing.kind}
                        onChange={(e) => setEditingProduct((prev) => ({ ...prev, [s.id]: { ...prev[s.id], kind: e.target.value } }))}
                        data-testid={`edit-kind-${s.id}`}
                        className="mt-1 w-full bg-white border border-[#E8E6E1] rounded px-2 py-1 text-sm">
                        <option value="libro">📖 Libro</option>
                        <option value="gioco">🧩 Gioco</option>
                        <option value="app">📱 App</option>
                      </select>
                    </label>
                    <label className="text-xs text-[#5C6B64]">
                      Età min
                      <input type="number" min={0} max={18} value={editing.age_min}
                        onChange={(e) => setEditingProduct((prev) => ({ ...prev, [s.id]: { ...prev[s.id], age_min: Number(e.target.value) } }))}
                        className="mt-1 w-full bg-white border border-[#E8E6E1] rounded px-2 py-1 text-sm" />
                    </label>
                    <label className="text-xs text-[#5C6B64]">
                      Età max
                      <input type="number" min={0} max={18} value={editing.age_max}
                        onChange={(e) => setEditingProduct((prev) => ({ ...prev, [s.id]: { ...prev[s.id], age_max: Number(e.target.value) } }))}
                        className="mt-1 w-full bg-white border border-[#E8E6E1] rounded px-2 py-1 text-sm" />
                    </label>
                    <label className="text-xs text-[#5C6B64] sm:col-span-2">
                      Autore / Brand
                      <input value={editing.brand_or_author}
                        onChange={(e) => setEditingProduct((prev) => ({ ...prev, [s.id]: { ...prev[s.id], brand_or_author: e.target.value } }))}
                        className="mt-1 w-full bg-white border border-[#E8E6E1] rounded px-2 py-1 text-sm" />
                    </label>
                    <label className="text-xs text-[#5C6B64] sm:col-span-2">
                      Descrizione
                      <textarea rows={3} value={editing.description}
                        onChange={(e) => setEditingProduct((prev) => ({ ...prev, [s.id]: { ...prev[s.id], description: e.target.value } }))}
                        className="mt-1 w-full bg-white border border-[#E8E6E1] rounded px-2 py-1 text-sm" />
                    </label>
                    <label className="text-xs text-[#5C6B64] sm:col-span-2">
                      Store link
                      <input value={editing.store_link}
                        onChange={(e) => setEditingProduct((prev) => ({ ...prev, [s.id]: { ...prev[s.id], store_link: e.target.value } }))}
                        className="mt-1 w-full bg-white border border-[#E8E6E1] rounded px-2 py-1 text-sm" />
                    </label>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => savePublishedProductEdit(s)}
                      disabled={!!savingEdit[s.id]}
                      data-testid={`save-edit-${s.id}`}
                      className="btn-primary text-xs px-3 py-1.5 disabled:opacity-50">
                      {savingEdit[s.id] ? "Salvataggio…" : "💾 Salva modifiche"}
                    </button>
                    <button onClick={() => cancelEditPublished(s.id)}
                      className="btn-secondary text-xs px-3 py-1.5">Annulla</button>
                  </div>
                </div>
              )}
            </div>
            );
          })
        )}

        {/* All reviews tab — live moderation */}
        {tab === "all-reviews" && !loadingData && (
          allReviews.items.length === 0 ? (
            <div className="card-surface p-10 text-center">
              <CheckCircle2 className="mx-auto text-[#1A362D]" size={32} />
              <p className="mt-3 font-display text-2xl text-[#1A362D]">Nessuna esperienza</p>
              <p className="mt-2 font-ui text-sm text-[#5C6B64]">Con questi filtri non ci sono esperienze da mostrare.</p>
            </div>
          ) : (
            <>
              <p className="font-ui text-sm text-[#5C6B64] mb-4">
                <span data-testid="all-reviews-total" className="font-semibold text-[#1A362D]">{allReviews.total}</span> esperienze totali · Mostrate {allReviews.items.length} (da {reviewsSkip + 1})
              </p>
              {allReviews.items.map((r) => (
                <div key={`${r.kind}-${r.id}`} className="card-surface p-5" data-testid={`all-review-${r.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-wider font-ui px-2 py-0.5 rounded ${r.kind === "school" ? "bg-[#DCE4DC] text-[#1A362D]" : "bg-[#FFE9D9] text-[#C05C45]"}`}>
                        {r.kind === "school" ? "🏫 Scuola" : "📚 Prodotto"}
                      </span>
                      <span className="text-[11px] font-ui text-[#E0A930]">{"★".repeat(r.rating || 0)}{"☆".repeat(5 - (r.rating || 0))}</span>
                      <span className="text-xs text-[#8A9691] font-ui">{r.created_at ? new Date(r.created_at).toLocaleString("it-IT") : "—"}</span>
                    </div>
                    {r.kind === "school" && r.target_id ? (
                      <Link to={`/scuola/${r.target_id}`} className="text-xs font-ui text-[#C05C45] hover:underline inline-flex items-center gap-1"><Eye size={11} /> {r.target_name} — {r.target_city}</Link>
                    ) : r.kind === "product" && r.target_slug ? (
                      <Link to={`/prodotto/${r.target_slug}`} className="text-xs font-ui text-[#C05C45] hover:underline inline-flex items-center gap-1"><Eye size={11} /> {r.target_name}</Link>
                    ) : null}
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    {r.user_picture ? (
                      <img src={r.user_picture} alt={r.user_name} className="w-7 h-7 rounded-full object-cover" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-[#F2EFEB] flex items-center justify-center font-ui text-xs text-[#1A362D]">{(r.user_name || "?")[0]?.toUpperCase()}</div>
                    )}
                    <p className="font-ui text-sm font-medium text-[#1A362D]">{r.user_name || "—"}</p>
                  </div>
                  <p className="mt-3 font-ui text-sm text-[#1A362D] whitespace-pre-line leading-relaxed">{r.comment}</p>
                  <div className="mt-4 flex gap-2">
                    <button
                      onClick={() => adminDeleteReview(r.kind, r.id, r.user_name)}
                      data-testid={`all-review-delete-${r.id}`}
                      className="btn-danger text-xs px-3 py-1.5 inline-flex items-center gap-1"
                    >
                      <Trash2 size={12} /> Elimina
                    </button>
                  </div>
                </div>
              ))}
              {/* Pagination */}
              {(reviewsSkip > 0 || allReviews.total > reviewsSkip + allReviews.items.length) && (
                <div className="flex items-center justify-between pt-4">
                  <button
                    onClick={() => setReviewsSkip(Math.max(0, reviewsSkip - REVIEWS_PAGE))}
                    disabled={reviewsSkip === 0}
                    data-testid="all-reviews-prev"
                    className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-40"
                  >
                    ← Precedente
                  </button>
                  <span className="font-ui text-xs text-[#8A9691]">Pagina {Math.floor(reviewsSkip / REVIEWS_PAGE) + 1}</span>
                  <button
                    onClick={() => setReviewsSkip(reviewsSkip + REVIEWS_PAGE)}
                    disabled={allReviews.total <= reviewsSkip + allReviews.items.length}
                    data-testid="all-reviews-next"
                    className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-40"
                  >
                    Successiva →
                  </button>
                </div>
              )}
            </>
          )
        )}

        {/* Corrections tab */}
        {tab === "corrections" && !loadingData && (
          corrections.length === 0 ? (
            <div className="card-surface p-10 text-center">
              <CheckCircle2 className="mx-auto text-[#1A362D]" size={32} />
              <p className="mt-3 font-display text-2xl text-[#1A362D]">Nessuna correzione</p>
              <p className="mt-2 font-ui text-sm text-[#5C6B64]">Quando i genitori segnalano correzioni di dati, le trovi qui.</p>
            </div>
          ) : corrections.map((c) => (
            <div key={c.id} className="card-surface p-5" data-testid={`correction-${c.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-wider font-ui px-2 py-0.5 rounded ${c.status === "open" ? "bg-[#C05C45] text-white" : c.status === "applied" ? "bg-[#1A362D] text-white" : "bg-[#E8E6E1] text-[#5C6B64]"}`}>{c.status}</span>
                  <span className="text-xs text-[#8A9691] font-ui">{new Date(c.created_at).toLocaleString("it-IT")}</span>
                </div>
                <Link to={`/scuola/${c.school_id}`} className="text-xs font-ui text-[#C05C45] hover:underline inline-flex items-center gap-1"><Eye size={11} /> {c.school_name} — {c.school_city}</Link>
              </div>

              <div className="mt-3 space-y-2">
                {Object.entries(c.diff).map(([field, vals]) => (
                  <div key={field} className="p-3 bg-[#F2EFEB] border border-[#E8E6E1] rounded-md">
                    <p className="label-eyebrow text-[#1A362D]">{field}</p>
                    <div className="mt-1 text-sm font-ui">
                      <p className="text-[#8A9691]">Vecchio: <span className="line-through">{vals.old || "—"}</span></p>
                      <p className="text-[#1A362D] font-medium">Nuovo: {vals.new}</p>
                    </div>
                  </div>
                ))}
              </div>

              {c.notes && <p className="mt-3 p-3 bg-white border border-[#E8E6E1] rounded-md font-ui text-sm text-[#1A362D]"><span className="text-[#8A9691]">Nota: </span>{c.notes}</p>}
              <p className="mt-3 font-ui text-xs text-[#8A9691] inline-flex items-center gap-1"><Mail size={11} /> Da: {c.contact_name || "—"} ({c.contact_email})</p>

              {c.status === "open" && (
                <div className="mt-4 flex gap-2">
                  <button onClick={() => resolveCorrection(c.id, "apply")} data-testid={`correction-apply-${c.id}`} className="btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1"><CheckCircle2 size={12} /> Applica tutto</button>
                  <button onClick={() => resolveCorrection(c.id, "reject")} data-testid={`correction-reject-${c.id}`} className="btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1"><X size={12} /> Rifiuta</button>
                </div>
              )}
              {c.applied_fields && c.applied_fields.length > 0 && (
                <p className="mt-2 text-xs font-ui text-[#5C6B64]">Applicati: {c.applied_fields.join(", ")}</p>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
